'use client';

import { useMemo, useSyncExternalStore } from 'react';
import { useRouter as useNextRouter } from 'next/navigation';

/**
 * Tracks page navigations started from the app, so a progress bar can show while the next page loads
 * and clicks are held back until it arrives (see NavigationProgressBar in app/layout.tsx).
 */

// A navigation that never lands (e.g. a failed load) must not block the page for long
const MAX_NAVIGATION_MS = 15000;

let isNavigating = false;
let safetyTimer: number | undefined;
const listeners = new Set<() => void>();

const notify = () => listeners.forEach((listener) => listener());

const basePath = process.env.NEXT_PUBLIC_BASE_PATH || '';

// Path and query only: a hash change does not load a new page
const toComparableUrl = (url: URL) => `${url.pathname.replace(/\/+$/, '')}${url.search}`;

/** True when the href would load a different page (path or query) on this site. */
export function isNewInternalPage(href: string): boolean {
  if (typeof window === 'undefined') return false;
  try {
    const withBasePath = href.startsWith('/') && basePath && !href.startsWith(`${basePath}/`) ? `${basePath}${href}` : href;
    const target = new URL(withBasePath, window.location.href);
    if (target.origin !== window.location.origin) return false;
    return toComparableUrl(target) !== toComparableUrl(new URL(window.location.href));
  } catch {
    return false;
  }
}

export function startNavigation(href: string) {
  if (!isNewInternalPage(href)) return;
  isNavigating = true;
  window.clearTimeout(safetyTimer);
  safetyTimer = window.setTimeout(finishNavigation, MAX_NAVIGATION_MS);
  notify();
}

export function finishNavigation() {
  window.clearTimeout(safetyTimer);
  if (!isNavigating) return;
  isNavigating = false;
  notify();
}

const subscribe = (listener: () => void) => {
  listeners.add(listener);
  return () => { listeners.delete(listener); };
};

export function useIsNavigating() {
  return useSyncExternalStore(subscribe, () => isNavigating, () => false);
}

type AppRouter = ReturnType<typeof useNextRouter>;

/** next/navigation's useRouter, with push/replace showing the page-loading progress bar. */
export function useRouter(): AppRouter {
  const router = useNextRouter();
  return useMemo(() => ({
    ...router,
    push: (href: string, options?: Parameters<AppRouter['push']>[1]) => {
      startNavigation(href);
      router.push(href, options);
    },
    replace: (href: string, options?: Parameters<AppRouter['replace']>[1]) => {
      startNavigation(href);
      router.replace(href, options);
    },
  }), [router]);
}
